import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Phone, MessageCircle, LifeBuoy, Package } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SUPPORT_FAQ = [
  { q: "How fast will you respond?", a: "We reply to every research desk inquiry within one business day." },
  { q: "Can you provide a COA before I order?", a: "Yes — reference the compound and desired batch and we'll email the current COA." },
  { q: "Do you ship internationally?", a: "We ship domestically only. International inquiries are reviewed case-by-case." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. Our team will follow up within one business day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="eyebrow mb-3">Contact · Research Desk</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.05]">
            Talk to our research team.
          </h1>
          <p className="mt-4 max-w-xl text-slate-600 text-base">
            Questions on documentation, batch records, or catalog inquiries. We respond within one business day.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <form onSubmit={submit} className="lg:col-span-7 border border-slate-200 rounded-md p-8 bg-white" data-testid="contact-form">
          <div className="eyebrow mb-4">Send a Message</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Name</label>
              <Input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-md border-slate-300" placeholder="Dr. Jane Doe" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Email</label>
              <Input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-md border-slate-300" placeholder="jane@lab.com" />
            </div>
          </div>
          <div className="mt-4">
            <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Subject</label>
            <Input data-testid="contact-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2 rounded-md border-slate-300" placeholder="COA for BPC-157, batch P4P-2026-041" />
          </div>
          <div className="mt-4">
            <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Message</label>
            <Textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 rounded-md border-slate-300" placeholder="Your inquiry…" />
          </div>
          <Button data-testid="contact-submit" type="submit" disabled={submitting} className="mt-6 h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
            {submitting ? "Sending…" : "Send message"}
          </Button>
        </form>

        <aside className="lg:col-span-5 space-y-4">
          <div className="border border-slate-200 rounded-md p-6 bg-white">
            <div className="eyebrow mb-4">Business Information</div>
            <div className="space-y-4">
              {[
                { i: Mail, t: "Research desk", v: "research@peps4pets.com" },
                { i: Phone, t: "Phone", v: "+1 (720) 555-0142" },
                { i: MapPin, t: "Facility", v: "Salt Lake City · USA" },
                { i: Clock, t: "Hours", v: "Mon–Fri · 8:00–18:00 MT" },
              ].map((c) => (
                <div key={c.t} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <c.i className="w-4 h-4 text-slate-700" strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{c.t}</div>
                    <div className="mt-1 text-sm text-slate-900">{c.v}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-slate-200 rounded-md p-6 bg-slate-50">
            <div className="eyebrow mb-4">Support Options</div>
            <div className="space-y-3">
              {[
                { i: MessageCircle, t: "Live inquiry chat", d: "Fastest path to a research desk analyst." },
                { i: Package, t: "Order tracking", d: "Real-time status inside the customer portal." },
                { i: LifeBuoy, t: "Documentation help", d: "COA lookups, batch reconciliation, storage." },
              ].map((c) => (
                <div key={c.t} className="flex items-start gap-3">
                  <c.i className="w-5 h-5 mt-0.5 text-blue-700" strokeWidth={1.5} />
                  <div>
                    <div className="text-sm font-medium text-slate-900">{c.t}</div>
                    <div className="text-xs text-slate-600">{c.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </section>

      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
          <SectionHeader eyebrow="FAQ" title="Before you reach out — common answers." align="center" />
          <Accordion type="single" collapsible className="border-t border-slate-200">
            {SUPPORT_FAQ.map((f, i) => (
              <AccordionItem key={i} value={`c-${i}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left text-slate-900 font-display text-lg hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
